/**
 * Os limites da integração DV360, num lugar só (P-43, P-55, §6 C5 do plano aprovado; D-191).
 *
 * Os tetos permanecem constantes de CÓDIGO. Os parâmetros de ativação e da agenda intradiária
 * não os alteram. Mudar um teto é release, com teste, e nunca um ajuste de
 * operação que ninguém revisa. Atingido um limite, nada parcial é gravado, a cobertura não muda e
 * todo bloqueio tem saída automática ou ação nomeada (§6 C5, "Ao atingir um limite").
 *
 * Quem lê estes números: o parser (bytes, linhas, casas do custo), as janelas e a cobertura
 * (`janelas.ts`), o planejador e os consumidores da API (cadência, voo, teto diário, ritmo), a
 * descoberta das Campaigns (`descoberta.ts` e o catálogo da API) e a tela (Campaigns por
 * campanha). Ninguém os copia. Este é módulo da C1: mudar um número aqui custa outra rodada dela.
 */

/** As três origens de uma execução de relatório, na ordem de prioridade do teto diário. */
export const PRIORIDADE_DAS_EXECUCOES_DV360 = ['diaria', 'manual', 'historico'] as const
export type OrigemDaExecucaoDv360 = (typeof PRIORIDADE_DAS_EXECUCOES_DV360)[number]

export const LIMITES_DV360 = {
  /** Uma janela de relatório tem no máximo 32 dias (igual à janela diária [D−32, D−1]). */
  diasPorJanela: 32,
  /** Download lido em fluxo, com contador, abortado no byte seguinte a 4 MiB (4 × 1024 × 1024). */
  bytesPorDownload: 4_194_304,
  /** Linhas de dados por relatório (a linha de total e o rodapé não contam). */
  linhasPorRelatorio: 50_000,
  /** Casas decimais do Media Cost no arquivo. Mais do que isso invalida o relatório (§4.6). */
  casasDoCusto: 6,
  /** Relatórios em voo no sistema inteiro. */
  relatoriosEmVoo: 10,
  /** Execuções de relatório por dia, com a prioridade de `PRIORIDADE_DAS_EXECUCOES_DV360`. */
  execucoesPorDia: 200,
  /** Ritmo do cliente: requisições por segundo, por API (DV360 e Bid Manager separadas). */
  requisicoesPorSegundo: 2,
  /** Campaigns DV360 vinculadas a uma campanha local. */
  campaignsPorCampanha: 20,
  /** Acima disto, alerta: sobra folga de 50 execuções para histórico e manual. */
  alertaDeCampaignsEmSincronizacao: 150,
  /**
   * O catálogo do Partner que a descoberta lê (D-192; DS-684 (a) e (b)), em memória no processo da API e no MESMO ritmo
   * de `requisicoesPorSegundo` (a DS-664: sem raia própria). Cada lista de Campaigns de um Advertiser tem 20 s para
   * todas as páginas; até 5 listas em voo (a primeira sai sozinha, pelo token); o ciclo inteiro tem teto de 120 s; a
   * entrada de um Advertiser vale 10 min (depois, o retrato vencido é servido e relido por trás).
   */
  segundosPorListaDoCatalogo: 20,
  minutosDeValidadeDoCatalogo: 10,
  listasDoCatalogoEmVoo: 5,
  segundosDoCicloDoCatalogo: 120,
  /** A busca da descoberta devolve páginas de 50 Campaigns. */
  campaignsPorPaginaDaDescoberta: 50,
  /** Confirmar vínculo aceita só a Campaign cujo Advertiser foi lido pelo servidor há 60 min ou menos. */
  minutosParaConfirmarPeloCatalogo: 60,
  /** "Atualizar lista" força um ciclo no máximo a cada 60 s por processo. */
  segundosEntreAtualizacoesForcadas: 60,
  /** Depois de um ciclo parcial ou falho, o próximo espera ao menos 30 s (na cota, o Retry-After, se for maior). */
  segundosDepoisDeFalha: 30,
  /** O aquecimento: um ciclo 60 s depois de a API subir, com a integração ativa e o Partner configurado. */
  segundosParaAquecer: 60,
  /** Espera por relatório: alerta em 3 h, expira em 20 h (cadência em `CADENCIA_DE_ESPERA_DV360`). */
  minutosParaAlertaDeEspera: 3 * 60,
  minutosParaExpirar: 20 * 60,
  /** Versões substituídas de um fato: 45 dias (31 de revisão + 14 para detectar e desfazer). */
  diasDeRetencaoDasSubstituidas: 45,
  /** Ausência confirmada: 2 relatórios validados sem a linha, o segundo ≥ 20 h depois do primeiro. */
  observacoesParaConfirmarAusencia: 2,
  horasEntreObservacoesDeAusencia: 20,
  /** Carga histórica automática: 12 janelas, nunca antes de D − 24 meses. */
  janelasDaCargaAutomatica: 12,
  mesesDeHistoricoNoGoogle: 24,
  /** O histórico começa 7 dias antes da menor data planejada (gasto 1 dia antes do planejado [R]). */
  diasAntesDoPlanejado: 7,
  /** Fim delimitado da sincronização (§4.7): fim local + 32, teto + 92. */
  diasDoFimBase: 32,
  diasDoTetoDoFim: 92,
  /** "Retomar acompanhamento": hoje + 92; o implícito do vínculo tardio: hoje + 32. */
  diasDeRetomada: 92,
  diasDeRetomadaImplicita: 32,
  /** O relatório FAILED tem uma nova tentativa da mesma janela 30 min depois do término (§6 C5). */
  minutosParaRepetirFailed: 30,
  /** A conferência do vínculo que ainda aguarda, depois de uma falha, tenta de novo em 30 min (DS-671 (b)). */
  minutosParaRepetirConferencia: 30,
  /** A Campaign suspensa por limite ou validação tem uma tentativa automática por semana (§6 C5). */
  diasEntreTentativasAutomaticas: 7,
  /** Os metadados de um criativo são relidos quando têm mais de 7 dias (P-53; DS-671 (d)). */
  diasParaRelerCriativo: 7,
  /** Alertas da coleta (§6 C5, P-42; DS-672): 3 falhas seguidas, a cota em 2 dias seguidos, a revisão sem validado há 48 h. */
  falhasSeguidasParaAlerta: 3,
  diasDeCotaParaAlerta: 2,
  horasDaRevisaoAtrasada: 48,
  /** A série por período da seção "Mídia · DV360": até 366 pontos (§5.2). */
  pontosDaSerie: 366,
  /** Os line items de um IO e os criativos do escopo, por página (§5.2). */
  itensPorPagina: 50,
} as const

/**
 * Cadência do `reports.get` (§6 C5): a cada 5 min até 30 min do `run`, a cada 15 min até 3 h e a
 * cada 60 min até 20 h. Ordenada pelo limite.
 */
export const CADENCIA_DE_ESPERA_DV360 = [
  { ateMinutos: 30, aCadaMinutos: 5 },
  { ateMinutos: 3 * 60, aCadaMinutos: 15 },
  { ateMinutos: 20 * 60, aCadaMinutos: 60 },
] as const

export type PassoDaEsperaDv360 =
  | { readonly acao: 'consultar'; readonly emMinutos: number; readonly alertar: boolean }
  | { readonly acao: 'expirar' }

/**
 * O próximo passo da espera por um relatório, dado quantos minutos se passaram desde o `run`.
 * Nenhuma espera acontece dentro de um job (§6 C5, "Duração"): a varredura chama isto, agenda e sai.
 */
export function passoDaEsperaDv360(minutosDesdeORun: number): PassoDaEsperaDv360 {
  if (!Number.isFinite(minutosDesdeORun) || minutosDesdeORun < 0) {
    throw new RangeError(`minutos desde o run inválidos: ${String(minutosDesdeORun)}`)
  }
  const alertar = minutosDesdeORun >= LIMITES_DV360.minutosParaAlertaDeEspera
  for (const faixa of CADENCIA_DE_ESPERA_DV360) {
    if (minutosDesdeORun < faixa.ateMinutos) return { acao: 'consultar', emMinutos: faixa.aCadaMinutos, alertar }
  }
  return { acao: 'expirar' }
}

export interface PedidoDeExecucaoDv360<T> {
  readonly origem: OrigemDaExecucaoDv360
  readonly item: T
}

export interface SelecaoDoDiaDv360<T> {
  readonly executar: readonly PedidoDeExecucaoDv360<T>[]
  /** Passaram do teto: ficam `adiados` para o dia seguinte, na mesma prioridade. */
  readonly adiar: readonly PedidoDeExecucaoDv360<T>[]
}

/**
 * O teto diário com prioridade (§6 C5): primeiro a diária, depois a manual, depois o histórico;
 * dentro da mesma origem, a ordem de chegada. `jaExecutadasHoje` são as que já contaram no dia.
 */
export function selecionarExecucoesDoDiaDv360<T>(
  pedidos: readonly PedidoDeExecucaoDv360<T>[],
  jaExecutadasHoje: number,
): SelecaoDoDiaDv360<T> {
  if (!Number.isInteger(jaExecutadasHoje) || jaExecutadasHoje < 0) {
    throw new RangeError(`execuções do dia inválidas: ${String(jaExecutadasHoje)}`)
  }
  const ordenados = PRIORIDADE_DAS_EXECUCOES_DV360.flatMap((origem) => pedidos.filter((p) => p.origem === origem))
  const executar: PedidoDeExecucaoDv360<T>[] = []
  const adiar: PedidoDeExecucaoDv360<T>[] = []
  let contadas = jaExecutadasHoje
  for (const pedido of ordenados) {
    if (contadas < LIMITES_DV360.execucoesPorDia) {
      executar.push(pedido)
      contadas++
    } else {
      adiar.push(pedido)
    }
  }
  return { executar, adiar }
}

/**
 * O teto do dia com a RESERVA da diária (§6 C5, "prioridade: diária, depois manual, depois histórico"; DS-671 (j)): a
 * execução de uma sincronização diária cabe até `execucoesPorDia`; a das demais origens, só até `execucoesPorDia`
 * menos a reserva — uma execução por Campaign que ainda deve a diária de hoje. Assim, a carga histórica e o manual da
 * madrugada nunca deixam a diária das 09:30 sem cota.
 */
export function cabeNoDiaDv360(fatos: {
  readonly diaria: boolean
  readonly execucoesHoje: number
  readonly reservaDaDiaria: number
}): boolean {
  for (const [nome, n] of [['execuções do dia', fatos.execucoesHoje], ['reserva da diária', fatos.reservaDaDiaria]] as const) {
    if (!Number.isInteger(n) || n < 0) throw new RangeError(`${nome} inválida: ${String(n)}`)
  }
  // Em `bigint`: a regra deste pacote proíbe aritmética com `number` (contagem também não precisa dela).
  const reservadas = fatos.diaria ? 0n : BigInt(fatos.reservaDaDiaria)
  return BigInt(fatos.execucoesHoje) + reservadas < BigInt(LIMITES_DV360.execucoesPorDia)
}

/** Um relatório novo pode entrar em voo agora, sem passar de `relatoriosEmVoo`? */
export function cabeEmVooDv360(emVoo: number): boolean {
  return emVoo < LIMITES_DV360.relatoriosEmVoo
}
